import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { UploadPlanGuidance } from "@/components/common/UploadPlanGuidance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { UploadQuotaExceededEventDetail } from "@/lib/uploadQuotaEvents";

const formatBytesToMb = (bytes: number | null): string | null => {
  if (bytes === null || bytes < 0) {
    return null;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

type UploadQuotaExceededDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: UploadQuotaExceededEventDetail | null;
};

export const UploadQuotaExceededDialog = ({
  open,
  onOpenChange,
  detail,
}: UploadQuotaExceededDialogProps) => {
  const { t } = useTranslation();

  const summaryText = useMemo(() => {
    const limit = formatBytesToMb(detail?.limitBytes ?? null);
    const used = formatBytesToMb(detail?.usedBytes ?? null);
    const requested = formatBytesToMb(detail?.requestedBytes ?? null);

    if (!limit || !used || !requested) {
      return null;
    }

    return t("common.uploadQuotaExceededSummary", {
      limit,
      used,
      requested,
    });
  }, [detail, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="size-5" />
            {t("common.uploadQuotaExceededTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("common.uploadQuotaExceededDescription")}
          </DialogDescription>
        </DialogHeader>

        {summaryText && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {summaryText}
          </div>
        )}

        <UploadPlanGuidance />

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
